/**
 * useCompletedFormPdf.ts
 *
 * The Download PDF and Print buttons' state for a completed e-form, shared by
 * the patient chart's response modal and the forms inbox modal. Each modal
 * passes the request for its own endpoint and the setter for its own footer
 * message; the fetching, saving and print tab live in completedFormPdf.ts.
 */
import { useMutation } from '@tanstack/react-query';
import {
  COMPLETED_FORM_PDF_ERROR,
  COMPLETED_FORM_PDF_POPUP_BLOCKED,
  downloadCompletedFormPdf,
  openPrintWindow,
  showCompletedFormPdfInWindow,
  type CompletedFormPdf,
} from '@/services/completedFormPdf';

export interface CompletedFormPdfMessage {
  kind: 'error';
  message: string;
}

export interface UseCompletedFormPdfOptions {
  /** Fetches the PDF from the modal's endpoint. */
  fetchPdf: () => Promise<CompletedFormPdf>;
  /** The modal's footer message: cleared when an action starts, set when one fails. */
  setToast: (toast: CompletedFormPdfMessage | null) => void;
}

export interface UseCompletedFormPdf {
  /** Saves the PDF, under the server's name or `fallbackName` when it cannot be read. */
  download: (fallbackName: string) => void;
  /** Opens the print tab inside the click, then loads the PDF into it. */
  print: () => void;
  downloadPending: boolean;
  printPending: boolean;
  /** Either is running: both buttons are disabled meanwhile. */
  pending: boolean;
}

export function useCompletedFormPdf({ fetchPdf, setToast }: UseCompletedFormPdfOptions): UseCompletedFormPdf {
  const showError = (err: unknown) => {
    setToast({ kind: 'error', message: err instanceof Error ? err.message : COMPLETED_FORM_PDF_ERROR });
  };

  const downloadMutation = useMutation({
    mutationFn: (fallbackName: string) => downloadCompletedFormPdf(fetchPdf, fallbackName),
    onError: showError,
  });

  const printMutation = useMutation({
    mutationFn: (win: Window) => showCompletedFormPdfInWindow(win, fetchPdf),
    onError: showError,
  });

  const download = (fallbackName: string) => {
    setToast(null);
    downloadMutation.mutate(fallbackName);
  };

  const print = () => {
    setToast(null);
    // Opened here, inside the click, before any await: a later window is blocked as a pop-up.
    const win = openPrintWindow();
    if (!win) {
      setToast({ kind: 'error', message: COMPLETED_FORM_PDF_POPUP_BLOCKED });
      return;
    }
    printMutation.mutate(win);
  };

  return {
    download,
    print,
    downloadPending: downloadMutation.isPending,
    printPending: printMutation.isPending,
    pending: downloadMutation.isPending || printMutation.isPending,
  };
}
